// FlowCard — shared card shell for the Phase-2 renderers (ranked / chain /
// by_expiry / breakdown): platform panel chrome (dilettaPanelSx), a
// DILETTA_TITLE heading with an optional amber badge (chain's as-of date), and
// an optional caption. Keeps the four renderers visually identical without
// each re-declaring the chrome.

import { ReactNode } from 'react';
import { Box, Stack } from '@mui/material';

import { tokens } from '../../../../styles/tokens';
import {
  dilettaPanelSx,
  DILETTA_TITLE,
} from '../../../../components/v2/widgets/exotics-shared/diletta-chrome';
import { WindowBadge } from '../window-badge';

interface FlowCardProps {
  title: string;
  caption?: string;
  badge?: string;
  // The window chip reflects the global selected window (from context). Pass
  // `windowOverride` for a card that doesn't follow the selector (unusual_activity
  // -> "live"); pass `hideWindow` to suppress it entirely.
  windowOverride?: string;
  hideWindow?: boolean;
  children: ReactNode;
}

export const FlowCard = ({
  title,
  caption,
  badge,
  windowOverride,
  hideWindow,
  children,
}: FlowCardProps) => (
  <Box
    sx={{
      ...dilettaPanelSx,
      padding: '14px 16px 12px',
      height: '100%',
      boxSizing: 'border-box',
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
      <Box sx={{ fontSize: 12.5, fontWeight: 700, color: DILETTA_TITLE }}>{title}</Box>
      {badge && (
        <Box
          sx={{
            fontSize: 9,
            color: tokens.flow.accent,
            border: `1px solid ${tokens.flow.accent}66`,
            borderRadius: '4px',
            padding: '1px 6px',
            fontWeight: 700,
            letterSpacing: '0.4px',
          }}
        >
          {badge}
        </Box>
      )}
      <Box sx={{ flexGrow: 1 }} />
      {!hideWindow && <WindowBadge override={windowOverride} />}
    </Stack>
    {caption && (
      <Box sx={{ fontSize: 10.5, color: tokens.text.muted, margin: '2px 0 8px' }}>{caption}</Box>
    )}
    {children}
  </Box>
);
